import { TruckIcon } from "@heroicons/react/24/outline";
import { useForm } from "react-hook-form";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import SelectInput from "../shared/Inputs/SelectInput";
import { IVehicle } from "../../models/Vehicle";
import { VehicleProductionLine } from "../../models/ProductionPlanner";
import { useLabelGenerator } from "../../hooks/useLabelGenerator";
import useNotification from "../notifications/useNotifications";
import { useEffect, useMemo, useState } from "react";
import printHtml from "../../utils/printHtml";
import openInNewTab from "../../utils/openInNewTab";
import useVehiclesMutations from "../../data/useVehiclesMutations";

const productionLineOptions = [
  { id: VehicleProductionLine.MainLine, name: "Main Line" },
  { id: VehicleProductionLine.OffLine, name: "Off Line" },
];

type DepollutionVehicleSummaryProps = {
  vehicle: IVehicle;
  completedCount: number;
  totalCount: number;
  depollutionLabel: string;
};

export default function DepollutionVehicleSummary({
  vehicle,
  completedCount,
  totalCount,
  depollutionLabel,
}: DepollutionVehicleSummaryProps) {
  const { generatePartLabel, generateVehicleLabel } = useLabelGenerator();
  const { addNotification } = useNotification();
  const { patchVehicle } = useVehiclesMutations();
  const [isPrintingVehicleLabel, setIsPrintingVehicleLabel] = useState(false);
  const [isPrintingPartsLabels, setIsPrintingPartsLabels] = useState(false);

  const { register, setValue, watch } = useForm<{ productionLine: VehicleProductionLine }>({
    defaultValues: { productionLine: vehicle.productionLine ?? VehicleProductionLine.MainLine },
  });
  const selectedProductionLine = watch("productionLine");

  // Persist the change whenever the dropdown moves away from the vehicle's saved value.
  useEffect(() => {
    const current = vehicle.productionLine ?? VehicleProductionLine.MainLine;
    if (selectedProductionLine === undefined || selectedProductionLine === current) return;

    patchVehicle.mutate(
      { vehicleId: vehicle.id, patch: { productionLine: selectedProductionLine } },
      {
        onError: () => {
          setValue("productionLine", current);
          addNotification({
            variant: "error",
            primaryText: "Unable to update production line",
          });
        },
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductionLine]);

  const completionPercentage = useMemo(() => {
    if (totalCount === 0) return 0;
    return Math.round((completedCount / totalCount) * 100);
  }, [completedCount, totalCount]);

  const detailItems = [
    { label: "Stock", value: vehicle.stockNumber || "-" },
    { label: "VIN", value: vehicle.vinVerified || vehicle.combinedVIN || "-" },
    { label: "Engine", value: vehicle.engineModelCode || "-" },
    { label: "Fuel", value: vehicle.fuelType || "-" },
    { label: "Colour", value: vehicle.colourCurrent || "-" },
    { label: "Mileage", value: vehicle.odometerInMiles != null ? `${vehicle.odometerInMiles.toLocaleString()} mi` : "-" },
  ];

  const handlePrintVehicleLabel = async () => {
    setIsPrintingVehicleLabel(true);
    try {
      const html = await generateVehicleLabel(vehicle);
      printHtml(html);
    } catch {
      addNotification({
        variant: "error",
        primaryText: "Unable to print vehicle label",
      });
    } finally {
      setIsPrintingVehicleLabel(false);
    }
  };

  const handlePrintAllPartLabels = async () => {
    const parts = (vehicle.parts ?? []).filter((part) => !!part);
    if (parts.length === 0) {
      addNotification({
        variant: "error",
        primaryText: "No parts found for this vehicle",
      });
      return;
    }

    setIsPrintingPartsLabels(true);
    try {
      const htmlPages = await Promise.all(
        parts.map(async (part) => {
          part.vehicle = vehicle;
          return generatePartLabel(part);
        })
      );

      const allHtml = htmlPages.join("<div style='page-break-after: always;'></div>");
      printHtml(allHtml);
    } catch {
      addNotification({
        variant: "error",
        primaryText: "Unable to print part labels",
      });
    } finally {
      setIsPrintingPartsLabels(false);
    }
  };

  return (
    <div className="p-4 flex flex-col gap-4">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-14 w-14 rounded-md bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-400">
            <img
              className="object-contain"
              src={vehicle.vehicleImageUrl}
            />
          </div>
          <div>
            <div className="text-2xl font-semibold tracking-wide">{vehicle.vrm}</div>
            <div className="text-sm text-gray-600">
              {vehicle.combinedMake} {vehicle.combinedModel}
            </div>
          </div>
        </div>

        <div className="min-w-[220px]">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="font-medium text-gray-700">{depollutionLabel} Progress</span>
            <span className="text-gray-600">
              {completedCount}/{totalCount} complete
            </span>
          </div>
          <div className="h-2.5 rounded-full bg-gray-200 overflow-hidden">
            <div
              className="h-full bg-primary-600 transition-all"
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {detailItems.map((item) => (
          <div key={item.label} className="rounded-md border border-gray-200 px-3 py-2">
            <div className="text-[11px] uppercase tracking-wide text-gray-500">{item.label}</div>
            <div className="text-sm font-medium text-gray-900 truncate">{item.value}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2 pt-6">
          <ButtonInput
            label="View Vehicle"
            isSubmit={false}
            onClick={() => openInNewTab(`/vehicles/${vehicle.id}`)}
            classes=""
          />
          <ButtonInput
            label="Print Vehicle Label"
            isSubmit={false}
            onClick={handlePrintVehicleLabel}
            isLoading={isPrintingVehicleLabel}
            classes=""
          />
          <ButtonInput
            label="Print All Part Labels"
            isSubmit={false}
            onClick={handlePrintAllPartLabels}
            isLoading={isPrintingPartsLabels}
            classes=""
          />
        </div>

        <div className="w-auto">
          <SelectInput
            label="Select Line"
            register={register}
            registerName="productionLine"
            options={productionLineOptions}
            setValue={setValue}
            watch={watch}
            cols="w-full"
            disabled={patchVehicle.isLoading}
            rounded
          />
        </div>
      </div>
    </div>
  );
}
